import { API_URL } from '../utils/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Plus, Pencil, Trash2, Calendar, CheckCircle2, Circle, Clock, Pause,
  ChevronLeft, ChevronRight, AlertCircle, X, User, 
  MoreVertical, Link, Search, ListTodo, Check, Square, CheckSquare
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '../lib/utils';


const PRIORITY_CONFIG = {
  low: { label: 'Düşük', color: 'border-l-slate-400', badge: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' },
  normal: { label: 'Normal', color: 'border-l-blue-400', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-400' },
  high: { label: 'Yüksek', color: 'border-l-orange-400', badge: 'bg-orange-100 text-orange-700', dot: 'bg-orange-400' },
  urgent: { label: 'Acil', color: 'border-l-red-500', badge: 'bg-red-100 text-red-700', dot: 'bg-red-500' }
};

const COLUMNS = [
  { id: 'pending', title: 'Yapılacak', icon: Circle, color: 'text-yellow-600', bgColor: 'bg-yellow-50/80', headerBg: 'bg-yellow-100' },
  { id: 'in_progress', title: 'Devam Ediyor', icon: Clock, color: 'text-blue-600', bgColor: 'bg-blue-50/80', headerBg: 'bg-blue-100' },
  { id: 'waiting', title: 'Beklemede', icon: Pause, color: 'text-purple-600', bgColor: 'bg-purple-50/80', headerBg: 'bg-purple-100' },
  { id: 'completed', title: 'Tamamlandı', icon: CheckCircle2, color: 'text-green-600', bgColor: 'bg-green-50/80', headerBg: 'bg-green-100' }
];

const Todos = () => {
  const { user, token } = useAuth();
  const [todos, setTodos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [editingTodo, setEditingTodo] = useState(null);
  
  // Filters
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Drag & Drop
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  
  // Quick Add
  const [quickAddColumn, setQuickAddColumn] = useState(null);
  const [quickAddText, setQuickAddText] = useState('');
  
  // Customer Search
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  // Checklist
  const [newChecklistItem, setNewChecklistItem] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'normal',
    status: 'pending',
    customer_id: '',
    customer_name: '',
    checklist: []
  });

  const canManage = user?.permissions?.includes('all') || user?.permissions?.includes('workspace_manage');
  const canView = user?.permissions?.includes('all') || user?.permissions?.includes('workspace_view') || canManage;

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;

      const [todosRes, calendarRes, statsRes, customersRes] = await Promise.all([
        axios.get(`${API_URL}/api/todos`, { headers }),
        axios.get(`${API_URL}/api/todos/calendar?year=${year}&month=${month}`, { headers }),
        axios.get(`${API_URL}/api/todos/stats`, { headers }),
        axios.get(`${API_URL}/api/customers`, { headers })
      ]);

      setTodos(todosRes.data);
      setCalendarData(calendarRes.data);
      setStats(statsRes.data);
      setCustomers(customersRes.data || []);
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  }, [token, currentMonth]);

  useEffect(() => {
    if (canView) {
      fetchData();
    }
  }, [canView, fetchData]);

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  const goToToday = () => {
    setCurrentMonth(new Date());
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };
  const showAll = () => setSelectedDate(null);

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = (firstDay.getDay() + 6) % 7;
    const days = [];

    for (let i = 0; i < startPadding; i++) {
      days.push({ day: null, isCurrentMonth: false });
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      const dayTodos = calendarData[dateStr] || [];
      days.push({
        day: i,
        dateStr,
        isCurrentMonth: true,
        isToday: dateStr === new Date().toISOString().split('T')[0],
        todos: dayTodos,
        hasTodos: dayTodos.length > 0,
        pendingCount: dayTodos.filter(t => t.status !== 'completed').length
      });
    }

    return days;
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      due_date: selectedDate || '',
      priority: 'normal',
      status: 'pending',
      customer_id: '',
      customer_name: '',
      checklist: []
    });
    setEditingTodo(null);
    setCustomerSearch('');
  };

  const openModal = (todo = null, defaultStatus = 'pending') => {
    if (todo) {
      setEditingTodo(todo);
      setFormData({
        title: todo.title || '',
        description: todo.description || '',
        due_date: todo.due_date || '',
        priority: todo.priority || 'normal',
        status: todo.status || 'pending',
        customer_id: todo.customer_id || '',
        customer_name: todo.customer_name || '',
        checklist: todo.checklist || []
      });
      setCustomerSearch(todo.customer_name || '');
    } else {
      setFormData({
        title: '',
        description: '',
        due_date: selectedDate || new Date().toISOString().split('T')[0],
        priority: 'normal',
        status: defaultStatus,
        customer_id: '',
        customer_name: '',
        checklist: []
      });
      setEditingTodo(null);
      setCustomerSearch('');
    }
    setIsModalOpen(true);
  };

  // Kart detay modalı aç
  const openDetailModal = (todo) => {
    setSelectedTodo(todo);
    setIsDetailModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Başlık zorunludur');
      return;
    }

    try {
      if (editingTodo) {
        await axios.put(`${API_URL}/api/todos/${editingTodo.id}`, formData, { headers });
        toast.success('Görev güncellendi');
      } else {
        await axios.post(`${API_URL}/api/todos`, formData, { headers });
        toast.success('Görev oluşturuldu');
      }
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hata oluştu');
    }
  };

  // Quick Add
  const handleQuickAdd = async (columnId) => {
    if (!quickAddText.trim()) {
      setQuickAddColumn(null);
      return;
    }

    try {
      await axios.post(`${API_URL}/api/todos`, {
        title: quickAddText,
        due_date: selectedDate || new Date().toISOString().split('T')[0],
        priority: 'normal',
        status: columnId,
        checklist: []
      }, { headers });
      toast.success('Görev eklendi');
      setQuickAddText('');
      setQuickAddColumn(null);
      fetchData();
    } catch (error) {
      toast.error('Eklenemedi');
    }
  };

  // Status Update
  const updateStatus = async (todoId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/todos/${todoId}`, { status: newStatus }, { headers });
      fetchData();
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  const handleDelete = async (todoId) => {
    if (!window.confirm('Bu görevi silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/api/todos/${todoId}`, { headers });
      toast.success('Görev silindi');
      setIsDetailModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Silinemedi');
    }
  };

  // Checklist Functions
  const addChecklistItem = async () => {
    if (!newChecklistItem.trim() || !selectedTodo) return;
    
    try {
      const res = await axios.post(
        `${API_URL}/api/todos/${selectedTodo.id}/checklist`,
        { text: newChecklistItem },
        { headers }
      );
      
      setSelectedTodo(prev => ({
        ...prev,
        checklist: [...(prev.checklist || []), res.data]
      }));
      setNewChecklistItem('');
      fetchData();
    } catch (error) {
      toast.error('Eklenemedi');
    }
  };

  const toggleChecklistItem = async (itemId, isChecked) => {
    if (!selectedTodo) return;
    
    try {
      await axios.put(
        `${API_URL}/api/todos/${selectedTodo.id}/checklist/${itemId}`,
        { is_checked: !isChecked },
        { headers }
      );
      
      setSelectedTodo(prev => ({
        ...prev,
        checklist: prev.checklist.map(item =>
          item.id === itemId ? { ...item, is_checked: !isChecked } : item
        )
      }));
      fetchData();
    } catch (error) {
      toast.error('Güncellenemedi');
    }
  };

  const deleteChecklistItem = async (itemId) => {
    if (!selectedTodo) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/todos/${selectedTodo.id}/checklist/${itemId}`,
        { headers }
      );
      
      setSelectedTodo(prev => ({
        ...prev,
        checklist: prev.checklist.filter(item => item.id !== itemId)
      }));
      fetchData();
    } catch (error) {
      toast.error('Silinemedi');
    }
  };

  // Drag & Drop
  const handleDragStart = (e, todo) => {
    setDraggedItem(todo);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', todo.id);
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e, columnId) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    if (draggedItem && draggedItem.status !== columnId) {
      await updateStatus(draggedItem.id, columnId);
      toast.success(`"${draggedItem.title}" taşındı`);
    }
    setDraggedItem(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  const isOverdue = (todo) => {
    if (!todo.due_date || todo.status === 'completed') return false;
    return new Date(todo.due_date) < new Date(new Date().toDateString());
  };

  // Get checklist progress
  const getChecklistProgress = (checklist) => {
    if (!checklist || checklist.length === 0) return null;
    const checked = checklist.filter(item => item.is_checked).length;
    return { checked, total: checklist.length, percent: Math.round((checked / checklist.length) * 100) };
  };

  // Filtreleme
  const filteredTodos = selectedDate 
    ? todos.filter(t => t.due_date === selectedDate)
    : todos;

  const getColumnTodos = (columnId) => filteredTodos.filter(t => t.status === columnId);

  // Müşteri arama
  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone?.includes(customerSearch)
  ).slice(0, 10);

  const selectCustomer = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customer.name
    }));
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };

  const clearCustomer = () => {
    setFormData(prev => ({
      ...prev,
      customer_id: '',
      customer_name: ''
    }));
    setCustomerSearch('');
  };

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Bu sayfayı görüntüleme yetkiniz yok.</p>
      </div>
    );
  }

  const calendarDays = generateCalendarDays();
  const weekDays = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="todos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold">Yapılacaklar</h1>
          <p className="text-muted-foreground">
            {selectedDate ? `${formatDate(selectedDate)} - ${filteredTodos.length} görev` : `Tüm Görevler - ${filteredTodos.length} görev`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {stats && (
            <div className="flex gap-2 mr-2">
              {stats.overdue > 0 && (
                <Badge variant="destructive" className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {stats.overdue} Gecikmiş
                </Badge>
              )}
              <Badge variant="secondary">{stats.pending} Yapılacak</Badge>
              <Badge variant="secondary" className="bg-purple-100 text-purple-700">{stats.waiting || 0} Beklemede</Badge>
              <Badge variant="secondary" className="bg-green-100 text-green-700">{stats.completed} Tamamlandı</Badge>
            </div>
          )}
          {canManage && (
            <Button onClick={() => openModal()} data-testid="new-task-btn">
              <Plus className="h-4 w-4 mr-2" />
              Yeni Görev
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 flex gap-4 min-h-0">
        {/* Sol: Mini Takvim */}
        <div className="w-72 flex-shrink-0">
          <Card className="h-full flex flex-col">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="icon" onClick={prevMonth}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium">
                  {currentMonth.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
                </span>
                <Button variant="ghost" size="icon" onClick={nextMonth}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pb-2 flex-1 flex flex-col">
              <div className="grid grid-cols-7 gap-0.5 mb-1">
                {weekDays.map(day => (
                  <div key={day} className="text-center text-[10px] font-medium text-muted-foreground py-1">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-0.5">
                {calendarDays.map((day, idx) => (
                  <button
                    key={idx}
                    onClick={() => day.isCurrentMonth && setSelectedDate(day.dateStr)}
                    disabled={!day.isCurrentMonth}
                    className={cn(
                      "h-8 w-8 rounded text-xs flex flex-col items-center justify-center transition-colors relative",
                      !day.isCurrentMonth && "opacity-30",
                      day.isCurrentMonth && "hover:bg-muted",
                      day.isToday && "ring-2 ring-primary ring-offset-1",
                      selectedDate === day.dateStr && "bg-primary text-primary-foreground hover:bg-primary",
                      day.hasTodos && day.pendingCount > 0 && selectedDate !== day.dateStr && "bg-amber-100 dark:bg-amber-900/30"
                    )}
                  >
                    {day.day}
                    {day.hasTodos && day.pendingCount > 0 && selectedDate !== day.dateStr && (
                      <span className="absolute bottom-0.5 w-1.5 h-1.5 rounded-full bg-amber-500" />
                    )}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={goToToday} className="flex-1 text-xs">
                  Bugün
                </Button>
                <Button variant={selectedDate === null ? "default" : "outline"} size="sm" onClick={showAll} className="flex-1 text-xs">
                  Tümü
                </Button>
              </div>

              {/* İstatistik Özeti */}
              {stats && (
                <div className="mt-4 pt-4 border-t space-y-2">
                  <h4 className="text-xs font-medium text-muted-foreground">Özet</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-2">
                      <Circle className="h-3 w-3 text-yellow-500" />
                      <span>{stats.pending} Yapılacak</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-3 w-3 text-blue-500" />
                      <span>{stats.in_progress} Devam</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Pause className="h-3 w-3 text-purple-500" />
                      <span>{stats.waiting || 0} Beklemede</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      <span>{stats.completed} Tamam</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sağ: 4 Sütunlu Kanban Board */}
        <div className="flex-1 flex gap-3 overflow-x-auto pb-2" data-testid="kanban-board">
          {COLUMNS.map(column => {
            const columnTodos = getColumnTodos(column.id);
            const Icon = column.icon;
            
            return (
              <div
                key={column.id}
                data-testid={`column-${column.id}`}
                className={cn(
                  "flex-1 min-w-[260px] max-w-[320px] flex flex-col rounded-xl transition-all border",
                  column.bgColor,
                  dragOverColumn === column.id && "ring-2 ring-primary ring-offset-2 scale-[1.01]"
                )}
                onDragOver={(e) => handleDragOver(e, column.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, column.id)}
              >
                {/* Column Header */}
                <div className={cn("px-3 py-2.5 flex items-center justify-between rounded-t-xl", column.headerBg)}>
                  <div className="flex items-center gap-2">
                    <Icon className={cn("h-4 w-4", column.color)} />
                    <span className="font-semibold text-sm">{column.title}</span>
                    <Badge variant="secondary" className="h-5 px-1.5 text-xs font-medium">
                      {columnTodos.length}
                    </Badge>
                  </div>
                  {canManage && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7 hover:bg-white/50"
                      onClick={() => setQuickAddColumn(column.id)}
                      data-testid={`add-to-${column.id}`}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Quick Add Input */}
                {quickAddColumn === column.id && (
                  <div className="px-3 py-2 border-b bg-white/50">
                    <Input
                      placeholder="Görev adı yazın..."
                      value={quickAddText}
                      onChange={(e) => setQuickAddText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleQuickAdd(column.id);
                        if (e.key === 'Escape') setQuickAddColumn(null);
                      }}
                      autoFocus
                      className="mb-2 bg-white"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleQuickAdd(column.id)} className="flex-1">Ekle</Button>
                      <Button size="sm" variant="ghost" onClick={() => setQuickAddColumn(null)}>İptal</Button>
                    </div>
                  </div>
                )}

                {/* Cards */}
                <div className="flex-1 overflow-y-auto px-2 py-2 space-y-2">
                  {columnTodos.map(todo => {
                    const checklistProgress = getChecklistProgress(todo.checklist);
                    
                    return (
                      <div
                        key={todo.id}
                        data-testid={`todo-card-${todo.id}`}
                        draggable={canManage}
                        onDragStart={(e) => handleDragStart(e, todo)}
                        onClick={() => openDetailModal(todo)}
                        className={cn(
                          "bg-white rounded-lg p-3 shadow-sm border-l-4 cursor-pointer",
                          "hover:shadow-md transition-all group",
                          PRIORITY_CONFIG[todo.priority]?.color || 'border-l-gray-300',
                          isOverdue(todo) && "border-red-500 bg-red-50/80",
                          draggedItem?.id === todo.id && "opacity-50 scale-95"
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className={cn("w-2 h-2 rounded-full flex-shrink-0", PRIORITY_CONFIG[todo.priority]?.dot)} />
                              <p className={cn(
                                "font-medium text-sm leading-tight",
                                todo.status === 'completed' && "line-through text-muted-foreground"
                              )}>
                                {todo.title}
                              </p>
                            </div>
                            {todo.description && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2 pl-3.5">
                                {todo.description}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Checklist Progress */}
                        {checklistProgress && (
                          <div className="mt-2 pl-3.5">
                            <div className="flex items-center gap-2">
                              <CheckSquare className={cn(
                                "h-3.5 w-3.5",
                                checklistProgress.checked === checklistProgress.total ? "text-green-500" : "text-gray-400"
                              )} />
                              <span className={cn(
                                "text-xs font-medium",
                                checklistProgress.checked === checklistProgress.total ? "text-green-600" : "text-gray-500"
                              )}>
                                {checklistProgress.checked}/{checklistProgress.total}
                              </span>
                              <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={cn(
                                    "h-full rounded-full transition-all",
                                    checklistProgress.checked === checklistProgress.total ? "bg-green-500" : "bg-blue-500"
                                  )}
                                  style={{ width: `${checklistProgress.percent}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Meta Info */}
                        <div className="flex items-center gap-2 mt-2 pl-3.5 flex-wrap">
                          {todo.due_date && (
                            <span className={cn(
                              "text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded",
                              isOverdue(todo) ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"
                            )}>
                              <Calendar className="h-3 w-3" />
                              {formatDate(todo.due_date)}
                            </span>
                          )}
                          
                          {todo.customer_name && (
                            <span className="text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                              <User className="h-3 w-3" />
                              {todo.customer_name}
                            </span>
                          )}

                          {isOverdue(todo) && (
                            <Badge variant="destructive" className="text-[10px] h-5 px-1">
                              Gecikmiş
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {columnTodos.length === 0 && quickAddColumn !== column.id && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Icon className={cn("h-8 w-8 mx-auto mb-2 opacity-30", column.color)} />
                      <p className="text-xs">Görev yok</p>
                      {canManage && (
                        <Button 
                          variant="link" 
                          size="sm" 
                          onClick={() => setQuickAddColumn(column.id)}
                          className="mt-1 text-xs"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Ekle
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Yeni Görev Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTodo ? 'Görevi Düzenle' : 'Yeni Görev'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Başlık *</label>
              <Input
                placeholder="Görev başlığı..."
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                autoFocus
                data-testid="todo-title-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">Açıklama</label>
              <Textarea
                placeholder="Görev açıklaması..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>

            {/* Müşteri Seçimi */}
            <div className="relative">
              <label className="text-sm font-medium mb-1.5 block">
                <User className="h-3.5 w-3.5 inline mr-1" />
                Müşteri Bağla
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Müşteri ara..."
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerDropdown(true);
                  }}
                  onFocus={() => setShowCustomerDropdown(true)}
                  className="pl-9"
                />
                {formData.customer_id && (
                  <Button type="button" variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7" onClick={clearCustomer}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              {showCustomerDropdown && customerSearch && !formData.customer_id && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                  {filteredCustomers.length > 0 ? filteredCustomers.map(customer => (
                    <button key={customer.id} type="button" className="w-full px-3 py-2 text-left hover:bg-muted flex items-center gap-2 text-sm" onClick={() => selectCustomer(customer)}>
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{customer.name}</div>
                        <div className="text-xs text-muted-foreground">{customer.phone}</div>
                      </div>
                    </button>
                  )) : (
                    <div className="px-3 py-2 text-sm text-muted-foreground">Müşteri bulunamadı</div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Tarih</label>
                <Input type="date" value={formData.due_date} onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Öncelik</label>
                <Select value={formData.priority} onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low"><div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-slate-400" />Düşük</div></SelectItem>
                    <SelectItem value="normal"><div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-400" />Normal</div></SelectItem>
                    <SelectItem value="high"><div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-orange-400" />Yüksek</div></SelectItem>
                    <SelectItem value="urgent"><div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500" />Acil</div></SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Durum</label>
              <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending"><div className="flex items-center gap-2"><Circle className="h-4 w-4 text-yellow-500" />Yapılacak</div></SelectItem>
                  <SelectItem value="in_progress"><div className="flex items-center gap-2"><Clock className="h-4 w-4 text-blue-500" />Devam Ediyor</div></SelectItem>
                  <SelectItem value="waiting"><div className="flex items-center gap-2"><Pause className="h-4 w-4 text-purple-500" />Beklemede</div></SelectItem>
                  <SelectItem value="completed"><div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" />Tamamlandı</div></SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>İptal</Button>
              <Button type="submit" data-testid="save-todo-btn">{editingTodo ? 'Güncelle' : 'Oluştur'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Kart Detay Modal - Trello Tarzı */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedTodo && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <DialogTitle className="text-xl flex items-center gap-2">
                      <span className={cn("w-3 h-3 rounded-full flex-shrink-0", PRIORITY_CONFIG[selectedTodo.priority]?.dot)} />
                      {selectedTodo.title}
                    </DialogTitle>
                    <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                      {selectedTodo.customer_name && (
                        <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                          <User className="h-3 w-3 mr-1" />
                          {selectedTodo.customer_name}
                        </Badge>
                      )}
                      {selectedTodo.due_date && (
                        <Badge variant={isOverdue(selectedTodo) ? "destructive" : "secondary"}>
                          <Calendar className="h-3 w-3 mr-1" />
                          {formatDate(selectedTodo.due_date)}
                        </Badge>
                      )}
                      <Badge className={PRIORITY_CONFIG[selectedTodo.priority]?.badge}>
                        {PRIORITY_CONFIG[selectedTodo.priority]?.label}
                      </Badge>
                    </div>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Açıklama */}
                {selectedTodo.description && (
                  <div>
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <Pencil className="h-4 w-4" />
                      Açıklama
                    </h3>
                    <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                      {selectedTodo.description}
                    </p>
                  </div>
                )}

                {/* Kontrol Listesi (Checklist) */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <ListTodo className="h-4 w-4" />
                      Kontrol Listesi
                      {selectedTodo.checklist?.length > 0 && (
                        <span className="text-xs font-normal text-muted-foreground">
                          ({selectedTodo.checklist.filter(i => i.is_checked).length}/{selectedTodo.checklist.length})
                        </span>
                      )}
                    </h3>
                  </div>

                  {/* Progress Bar */}
                  {selectedTodo.checklist?.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-muted-foreground">
                          {Math.round((selectedTodo.checklist.filter(i => i.is_checked).length / selectedTodo.checklist.length) * 100)}%
                        </span>
                      </div>
                      <Progress 
                        value={(selectedTodo.checklist.filter(i => i.is_checked).length / selectedTodo.checklist.length) * 100} 
                        className="h-2"
                      />
                    </div>
                  )}

                  {/* Checklist Items */}
                  <div className="space-y-2">
                    {selectedTodo.checklist?.map((item) => (
                      <div 
                        key={item.id} 
                        className={cn(
                          "flex items-center gap-3 p-2 rounded-lg group hover:bg-muted/50 transition-colors",
                          item.is_checked && "bg-green-50/50"
                        )}
                      >
                        <button
                          onClick={() => toggleChecklistItem(item.id, item.is_checked)}
                          className={cn(
                            "w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0",
                            item.is_checked 
                              ? "bg-green-500 border-green-500 text-white" 
                              : "border-gray-300 hover:border-green-500"
                          )}
                        >
                          {item.is_checked && <Check className="h-3 w-3" />}
                        </button>
                        <span className={cn(
                          "flex-1 text-sm",
                          item.is_checked && "line-through text-muted-foreground"
                        )}>
                          {item.text}
                        </span>
                        {canManage && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 opacity-0 group-hover:opacity-100"
                            onClick={() => deleteChecklistItem(item.id)}
                          >
                            <X className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Add New Item */}
                  {canManage && (
                    <div className="mt-3 flex gap-2">
                      <Input
                        placeholder="Yeni madde ekle..."
                        value={newChecklistItem}
                        onChange={(e) => setNewChecklistItem(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addChecklistItem();
                          }
                        }}
                        className="flex-1"
                      />
                      <Button onClick={addChecklistItem} size="sm">
                        <Plus className="h-4 w-4 mr-1" />
                        Ekle
                      </Button>
                    </div>
                  )}

                  {selectedTodo.checklist?.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Henüz kontrol listesi maddesi yok
                    </p>
                  )}
                </div>

                {/* Durum Değiştir */}
                <div>
                  <h3 className="text-sm font-semibold mb-3">Durumu Değiştir</h3>
                  <div className="flex flex-wrap gap-2">
                    {COLUMNS.map(col => {
                      const Icon = col.icon;
                      const isActive = selectedTodo.status === col.id;
                      return (
                        <Button
                          key={col.id}
                          variant={isActive ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            updateStatus(selectedTodo.id, col.id);
                            setSelectedTodo(prev => ({ ...prev, status: col.id }));
                          }}
                          className={cn(isActive && col.headerBg)}
                        >
                          <Icon className={cn("h-4 w-4 mr-1", !isActive && col.color)} />
                          {col.title}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => { setIsDetailModalOpen(false); openModal(selectedTodo); }} className="flex-1">
                    <Pencil className="h-4 w-4 mr-2" />
                    Düzenle
                  </Button>
                  {canManage && (
                    <Button variant="destructive" onClick={() => handleDelete(selectedTodo.id)}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Sil
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Todos;
